import { h, isVNode } from 'vue';
import { MIN_COLUMN_WIDTH, type DataTableColumnDef } from '@alphadoc/alphadoc-table-grid';
import { DateTime, type DateTimeFormatOptions } from 'luxon';
import i18n from '@/i18n.ts';
import type { SQLDate } from '@/modules/common/types/sql-date.type.ts';
import SectionTableHighlightText from '@/modules/core/components/card/SectionTableHighlightText.vue';
import { SECTION_TABLE_CELL_PADDING_X } from '@/modules/core/consts/section-table-modal.const.ts';
import { ElementType } from '@/modules/viewer/enums/element-type.enum.ts';
import type { EnumOptions } from '@/modules/viewer/types/elements.type.ts';
import { LuxonUtil } from '@/modules/common/utils/luxon.util.ts';

type SectionTableColumnOptions<TData> = {
  type?: ElementType;
  cell?: (value: unknown, row: TData) => unknown;
  enumOptions?: EnumOptions;
  distinct?: boolean;
  chip?: boolean;
  minSize?: number;
};

export function sectionTableChipMinSize(contentWidth: number) {
  return Math.max(MIN_COLUMN_WIDTH, Math.ceil(contentWidth + SECTION_TABLE_CELL_PADDING_X));
}

export function renderSectionTableEmptyValue() {
  return h('span', { class: 'section-table-empty-value' }, String(i18n.global.t('core.sectionTable.emptyValue')));
}

function isSectionTableEmptyContent(content: unknown) {
  if (content == null) {
    return true;
  }

  if (typeof content === 'string') {
    return content.trim() === '';
  }

  return false;
}

function renderSectionTableCell(content: unknown, chip = false) {
  if (isSectionTableEmptyContent(content)) {
    return renderSectionTableEmptyValue();
  }

  if (isVNode(content)) {
    return chip ? h('div', { class: 'section-table-chip' }, [content]) : content;
  }

  if (typeof content === 'string' || typeof content === 'number' || typeof content === 'boolean') {
    return h(SectionTableHighlightText, { text: String(content), placement: 'bottom' });
  }

  return content;
}

export function sectionTableColumn<TData>(
  id: string,
  accessor: (row: TData) => unknown,
  size: number,
  options?: SectionTableColumnOptions<TData>
): DataTableColumnDef<TData> {
  return {
    id,
    header: `core.sectionTable.columns.${id}`,
    accessor,
    size,
    minSize: options?.minSize ?? size,
    enableSorting: true,
    enableColumnFilter: true,
    cell: (value, row) =>
      renderSectionTableCell(options?.cell ? options.cell(value, row) : value, Boolean(options?.chip)),
    meta: {
      type: options?.type ?? ElementType.String,
      ...(options?.enumOptions !== undefined ? { options: options.enumOptions } : {}),
      ...(options?.distinct ? { distinct: true } : {}),
    } as DataTableColumnDef<TData>['meta'],
  };
}

export function sectionTableDateColumn<TData>(
  id: string,
  accessor: (row: TData) => SQLDate | null | undefined,
  size: number,
  format: DateTimeFormatOptions = DateTime.DATETIME_SHORT
): DataTableColumnDef<TData> {
  return sectionTableColumn(id, accessor, size, {
    type: ElementType.Date,
    cell: (value) => {
      const date = value as SQLDate | null | undefined;

      if (!date) {
        return '';
      }

      return LuxonUtil.formatSQL(date, String(i18n.global.locale.value), format) ?? '';
    },
  });
}

export function sectionTableNumberColumn<TData>(
  id: string,
  accessor: (row: TData) => unknown,
  size: number
): DataTableColumnDef<TData> {
  return sectionTableColumn(id, accessor, size, {
    type: ElementType.Number,
  });
}
